import { Inject, Injectable } from '@angular/core';
import { RemoveResult } from '@awesome-cordova-plugins/file/ngx';
import {
    ConfigService,
    CONNECTION_ERROR_CODES,
    getUuidV4,
    isFileError,
    MatSerNumber,
    Mutable,
    NOTIFICATION_STATUS_CODE,
    NotificationIdentifier,
    NotificationInterface,
    ValueHelperService,
    DeviceIdService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { NotificationInstanceToken } from '../core/interfaces/notification.interface';
import { restoreLastOpenedForm } from '../store/actions/last-opened-form.actions';
import { getFormListEntryForNotificationFactory } from '../store/selectors/list.selector';
import { FormState } from '../store/state/form.state';
import { initialLastOpenedFormState, LastOpenedFormState } from '../store/state/last-opened-form.state';
import { FormsState } from '../store/state/pace-forms.state';
import { initialFormFilters } from '../types/form-filter';
import { FormIdentifier } from '../types/form-identifier';
import { FormList } from '../types/form-list';
import { FormParticipantCopies } from '../types/form-participant-copies';
import { FormSubmitStatus } from '../types/form-submit-status';
import { FormsConstants } from '../types/forms-constants';
import { initialParticipantCopyState, ParticipantCopy, ParticipantCopyId } from '../types/participant-copy';
import { initialParticipantCopyModalState } from '../types/participant-copy-modal';
import { Recipient } from '../types/recipient';
import { FormUtilities } from '../utilities/form-utilities';
import { ODataGetXmlModel } from './forms-odata.interface';
import { FormsReadService } from './forms-read.service';
import { FormsStorageService } from './forms-storage.service';
import { FormsWriteInstanceToken, FormsWriteInterface } from './forms-write.interface';
import { ServicePartQuestionnaireService } from './service-part-questionnaire.service';
import { XmlParserService } from './xml-parser.service';
import { XmlSerializerService } from './xml-serializer.service';
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;

export class FormStatusWarnings {
    public noFormSubmitted = false; // true if no form was submitted yet
    public openFormName = ''; // name of form that was opened, but not submitted
    public handOverFormName = ''; // name of form where handover was done
}

@Injectable({
    providedIn: 'root',
})
export class FormsService {
    private readonly editableStatusCodes = new Set([
        NOTIFICATION_STATUS_CODE.OSR,
        NOTIFICATION_STATUS_CODE.SOSR,
        NOTIFICATION_STATUS_CODE.TOBS,
        NOTIFICATION_STATUS_CODE.ROSR,
        NOTIFICATION_STATUS_CODE.FCC,
        NOTIFICATION_STATUS_CODE.RTOBS,
        NOTIFICATION_STATUS_CODE.FTOBS,
    ]);
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly configService: ConfigService,
        private readonly formsReadService: FormsReadService,
        @Inject(FormsWriteInstanceToken) private readonly formsWriteService: FormsWriteInterface,
        private readonly formsStorageService: FormsStorageService,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        private readonly store: Store<FormsState>,
        private readonly xmlParserService: XmlParserService,
        private readonly xmlSerializerService: XmlSerializerService,
        private readonly valueHelperService: ValueHelperService,
        private readonly deviceIdService: DeviceIdService,
        private readonly servicePartQuestionnaireService: ServicePartQuestionnaireService,
    ) {}

    public restoreLastOpenedForm(): void {
        this.formsStorageService
            .loadLastOpenedForm()
            .pipe(
                catchError((error) => {
                    // file error with code 1 means "file not found", ie. we did not save any last opened form yet. That is ok.
                    // In all other cases we log an error message
                    if (!(isFileError(error) && error.code === 1)) {
                        this.log.error(
                            `FormsService: constructor: Failed to restore last opened form state with error '${JSON.stringify(
                                error,
                            )}'`,
                        );
                    }
                    return of(initialLastOpenedFormState);
                }),
                take(1),
            )
            .subscribe((lastOpenedForm) => this.store.dispatch(restoreLastOpenedForm({ lastOpenedForm })));
    }

    /**
     * Load a list of forms for a given material and equipment combination
     *
     * @param materialId the material id for which the form list should be loaded
     * @param equipmentId the equipment id for which the form list should be loaded
     * @param notification the notification for which the form list should be loaded
     * @param fromCache if true, the form list will be loaded from localStorage, otherwise it will be loaded from the backend
     * @param serviceActivity the type of the form selected or assign to the user
     * @returns the form list for the given material and equipment combination
     */

    public getFormList(
        materialId: string,
        equipmentId: string,
        notification: NotificationIdentifier,
        fromCache: boolean,
        serviceActivity?: FormsConstants.SERVICE_ACTIVITY_TYPES,
    ): Observable<FormList> {
        return combineLatest([
            fromCache
                ? this.formsReadService.getFormListFromCache(materialId, equipmentId, notification)
                : this.formsReadService.getFormList(materialId, equipmentId, notification, serviceActivity),
            this.formsStorageService.loadFormSubmitStatus(notification),
        ]).pipe(
            map(([formList, submitStatus]) => {
                const submitResults = formList.entries.map(
                    (entry) => submitStatus[entry.id.formId] || SUBMIT_STATUS.UNKNOWN,
                );

                return {
                    ...formList,
                    entries: formList.entries.map((entry, index) => ({ ...entry, submitStatus: submitResults[index] })),
                };
            }),
            // Check for each entry if it is cached
            mergeMap((formList) =>
                formList.entries.length > 0
                    ? combineLatest(
                          formList.entries.map((entry) =>
                              this.formsStorageService.isFormXmlInCache(entry.id, formList.notification),
                          ),
                      ).pipe(
                          map((cacheResults) => ({
                              ...formList,
                              entries: formList.entries.map((entry, index) => ({
                                  ...entry,
                                  cached: cacheResults[index],
                              })),
                          })),
                      )
                    : of(formList),
            ),
        );
    }
    /**
     * Load form either from backend or from localStorage if backend is unavailable and stores it as the currently used
     * form.
     *
     * @param id the form identifier
     * @param notificationId the notification identifier
     * @param downloadMode the download mode (EDIT or PREVIEW)
     * @param type the form type
     * @param language the language in which the form should be loaded
     * @returns the form state
     */
    public getForm(
        id: FormIdentifier,
        notificationId: NotificationIdentifier,
        downloadMode: FormsConstants.FORMSTATUS,
        type: FormsConstants.FORMTYPE,
        language: string,
    ): Observable<FormState> {
        // Check if local state exists
        // - If yes: Load & dispatch local state
        // - If no: Request XML from FormsReadService and build new state out of the response
        // In both cases we need to check what the readonly-state should be because it depends on external factors, for
        // example if the notification is in the right state for editing or if the form was submitted.
        return this.formsStorageService.loadFormStateFromCache(id, notificationId).pipe(
            // we need to reset the isSaving property - naturally it will be 'true' after loading because it was 'true'
            // while we saved
            map((form) => ({ ...form, isSaving: false })),
            tap((form) => {
                this.xmlParserService.setXml(form.participantCopies[form.selectedParticipantCopyId].xml);
            }),
            catchError((error) => {
                // for SPQ, we should not send Get Xml request to backend
                // instead, we need to just get the form state from Store, which was already set using loadSpqDocSuccessReducer reducer
                if (FormUtilities.isServicePartQuestionnaire(type)) {
                    this.log.debug('FormsService: getForm: getting spq form state');
                    return this.servicePartQuestionnaireService.getQuestionnaireState();
                } else {
                    // file error with code 1 means "file not found", ie. file was not in cache. That is ok. In all other
                    // cases we log an error message, then we delete any invalid cached file and try to load from GW as
                    // error mitigation.
                    if (!(isFileError(error) && error.code === 1)) {
                        this.log.error(
                            `FormsService: getForm: Failed to restore cached form state for notification ${JSON.stringify(
                                notificationId,
                            )}, form ${JSON.stringify(id)} with error '${JSON.stringify(
                                error,
                            )}'; trying to load from backend instead!`,
                        );
                    }

                    return this.formsStorageService
                        .removeFormStateFromCache(id, notificationId)
                        .pipe(
                            mergeMap(() => this.getFormFromBackend(id, notificationId, downloadMode, type, language)),
                        );
                }
            }),
            mergeMap((form) =>
                this.determineReadonlyMode(id, notificationId, form.lockedBy, form.deviceId, downloadMode).pipe(
                    // TODO: Sync participants with confirmation/notification?
                    map((readOnlyReason) => ({
                        ...form,
                        readOnlyReason,
                        isLoading: false,
                    })),
                ),
            ),
            map((form) => {
                const participantTitles = this.valueHelperService.getParticipantTitles();

                return {
                    ...form,
                    participantTitles,
                };
            }),
        );
    }

    private getFormFromBackend(
        id: FormIdentifier,
        notificationId: NotificationIdentifier,
        downloadMode: FormsConstants.FORMSTATUS,
        type: FormsConstants.FORMTYPE,
        language: string,
    ): Observable<FormState> {
        return combineLatest([
            this.formsReadService.getForm(id, notificationId, downloadMode),
            // prologues are only replaced for Astoria maintenance and safety reports
            type === FormsConstants.FORMTYPE.MAINTENANCE || type === FormsConstants.FORMTYPE.SAFETY
                ? this.formsReadService.getPrologue(type, language)
                : of(''),
        ]).pipe(
            map(([{ partialFormState, xml }, prologue]) => {
                // For 832 and 834 forms, the prologue needs to replaced with a translated version
                const xmlWithReplacedPrologue =
                    partialFormState.type === FormsConstants.FORMTYPE.MAINTENANCE ||
                    partialFormState.type === FormsConstants.FORMTYPE.SAFETY
                        ? this.xmlSerializerService.replacePrologue(
                              partialFormState.formId.formId,
                              partialFormState.type,
                              xml,
                              prologue,
                          )
                        : xml;

                this.xmlParserService.setXml(xmlWithReplacedPrologue);

                const notification = this.notificationService.getNotificationStateByIdentifier(notificationId);
                if (typeof notification === 'undefined') {
                    throw new Error(
                        `FormsService: getFormFromBackend: Could not load notification '${JSON.stringify(
                            notificationId,
                        )}'`,
                    );
                }
                const supportParticipantCopies = this.configService
                    .getParticipantCopySupportByNotificationType(notification.notificationType)
                    .includes(partialFormState.type);

                const hasLock = this.configService.getUserFrontId() === partialFormState.lockedBy;
                let content = this.xmlParserService.getContent(
                    partialFormState.doctype,
                    partialFormState.type,
                    partialFormState.typeFilters,
                    notificationId,
                    supportParticipantCopies,
                    hasLock,
                    partialFormState.saveMode,
                );

                const safetyAssessmentFormId = this.configService.getSafetyAssessmentForm();
                if (FormUtilities.isSafetyAssessmentForm(id.formId, safetyAssessmentFormId)) {
                    content = {
                        ...content,
                        customerIdentification: {
                            ...content.customerIdentification,
                            orderNr: {
                                ...content.customerIdentification.orderNr,
                                value: `${notificationId.qmNum} (${notificationId.maNum})`,
                            },
                        },
                    };
                }

                const recipients: Recipient[] = [
                    {
                        id: getUuidV4(),
                        email: this.configService.getCseEmailFax(),
                        deletable: false,
                        editable: false,
                        selected: true,
                        defaultEmail: true,
                    },
                ].concat(
                    (notification?.emailSet ?? []).map((email) => ({
                        id: getUuidV4(),
                        email,
                        deletable: false,
                        editable: false,
                        selected: true,
                        defaultEmail: false,
                    })),
                );

                const formMaster: ParticipantCopy = {
                    ...initialParticipantCopyState,
                    firstName: 'Master',
                    lastName: 'Checklist',
                    email: this.configService.getCseEmailFax(),
                    id: FormsConstants.MASTER_PARTICIPANT_ID,
                    answers: content.answers,
                    customerIdentification: content.customerIdentification,
                    defectServicePartIdentification: content.defectServicePartIdentification,
                    remarksSection: content.remarksSection,
                    functionalElements: content.functionalElements,
                    options: content.options,
                    xml: this.xmlParserService.getXml(partialFormState.doctype),
                    hasCopy: true,
                };

                const participantCopies: Mutable<FormParticipantCopies> = {
                    master: formMaster,
                };

                const form: FormState = {
                    ...partialFormState,
                    systemCondition: content.systemCondition,
                    hintsForFilling: content.hintsForFilling,
                    content: content.contentBlocks,
                    checkpoints: content.checkpoints,
                    checkpointDependencies: content.checkpointDependencies,
                    optionDependencies: content.optionDependencies,
                    headings: content.headings,
                    doctype: content.doctype,
                    infoClass: content.infoClass,
                    orgData: content.orgData,
                    copyright: content.copyright,
                    instructions: content.instructions,
                    recipients,
                    // the participants are being added on the participant-copies.component
                    participantTitles: {},
                    loadingStatus: FormsConstants.FORM_LOADING_STATUS.LOADED,
                    isLoading: false,
                    errorDuringLoading: false,
                    isSaving: false,
                    readOnlyReason: FormsConstants.READONLY_REASON.NONE,
                    participantCopies,
                    typeFilters: content.typeFilters,
                    formFilters: initialFormFilters,
                    selectedParticipantCopyId: FormsConstants.MASTER_PARTICIPANT_ID, // initially the form master is selected with id 0
                    supportParticipantCopies,
                    participantCopyModal: {
                        ...initialParticipantCopyModalState,
                    },
                    accordionGroups: {},
                    escalationEmail: partialFormState.escalationEmail,
                    individualization: content.individualization,
                    enableManualIndividualization: false,
                    measuringEquipments: content.measuringEquipments,
                };

                return form;
            }),
        );
    }

    public getEquipmentSetForNotification(
        notificationId: NotificationIdentifier,
        mainEquipment: MatSerNumber,
    ): Observable<MatSerNumber[]> {
        return this.formsReadService.getEquipmentSetForNotification(notificationId, mainEquipment);
    }

    public cacheForm(
        id: FormIdentifier,
        notification: NotificationIdentifier,
        formType: FormsConstants.FORMTYPE,
        language: string,
        downloadMode: FormsConstants.FORMSTATUS,
    ): Observable<ODataGetXmlModel> {
        return combineLatest([
            this.formsReadService.getFormXml(id, notification, downloadMode),
            formType === FormsConstants.FORMTYPE.MAINTENANCE || formType === FormsConstants.FORMTYPE.SAFETY
                ? this.formsReadService.getPrologue(formType, language)
                : of(''),
        ]).pipe(
            take(1),
            map(([formXML]) => formXML),
        );
    }

    /**
     * Check if the form should be editable or in readonly mode
     *
     * @param id The form identifier
     * @param notificationId The notification for which the form is being loaded
     * @param lockedBy The GID of the user that has locked this form (if a user has the lock)
     * @param deviceId The deviceId of the device that has locked this form
     * @param downloadMode should the form be loaded in EDIT or PREVIEW mode?
     * @returns the reason why the form is readonly
     */
    public determineReadonlyMode(
        id: FormIdentifier,
        notificationId: NotificationIdentifier,
        lockedBy: string,
        deviceId: string,
        downloadMode?: FormsConstants.FORMSTATUS,
    ): Observable<FormsConstants.READONLY_REASON> {
        const status = this.notificationService.getStatusStateByIdentifier(notificationId);

        return combineLatest([
            this.formsStorageService.loadFormSubmitStatus(notificationId),
            this.store.select(getFormListEntryForNotificationFactory({ formId: id, notification: notificationId })),
        ]).pipe(
            take(1),
            map(([submitStatus, formListEntry]) => {
                const formType = FormUtilities.getFormTypeFromId(id.formId);
                // If the form is the older version of refreshed form
                if (submitStatus[id.formId] === SUBMIT_STATUS.REFRESHED && formListEntry?.isOldVersion) {
                    return FormsConstants.READONLY_REASON.REFRESHED_FORM_OLD_VERSION;
                }
                // A form that was submitted cannot be edited again
                if (
                    submitStatus[id.formId] === SUBMIT_STATUS.FINAL ||
                    submitStatus[id.formId] === SUBMIT_STATUS.FINAL_NOT_OK
                ) {
                    return FormsConstants.READONLY_REASON.FINAL;
                } else if (
                    submitStatus[id.formId] === SUBMIT_STATUS.INTERIM ||
                    submitStatus[id.formId] === SUBMIT_STATUS.INTERIM_NOT_OK
                ) {
                    return FormsConstants.READONLY_REASON.INTERIM;
                } else if (submitStatus[id.formId] === SUBMIT_STATUS.HANDOVER) {
                    return FormsConstants.READONLY_REASON.HANDOVER;
                }

                if (formListEntry?.previousSubmit) {
                    return FormsConstants.READONLY_REASON.PREVIOUS_SUBMIT;
                }

                // If the form was specifically opened in preview mode, we don't allow editing
                if (downloadMode === FormsConstants.FORMSTATUS.PREVIEW) {
                    return FormsConstants.READONLY_REASON.PREVIEW;
                }

                // If the form is in edit mode but lockedBy empty
                // Usecase : if you open form in locked mode in offline mode
                if (downloadMode === FormsConstants.FORMSTATUS.LOCK && !lockedBy) {
                    return FormsConstants.READONLY_REASON.NOT_LOCKED;
                }

                // If the user does not have the lock or the device does not have the lock, we don't allow editing
                const legacyDeviceId = this.configService.getEnablePaceFormsCrossDeviceSync()
                    ? this.deviceIdService.getLegacyDeviceId(this.configService.getUserFrontId())
                    : FormsConstants.NO_LOCK_DEVICE_ID;
                if (
                    (this.configService.getUserFrontId() !== lockedBy || legacyDeviceId !== deviceId) &&
                    !FormUtilities.isServicePartQuestionnaire(formType)
                ) {
                    return FormsConstants.READONLY_REASON.LOCK;
                }

                // If the notifications is not in the correct status, we should not allow editing
                if (
                    !FormUtilities.isSafetyAssessmentForm(id.formId, this.configService.getSafetyAssessmentForm()) &&
                    !this.editableStatusCodes.has(status.statusCode)
                ) {
                    return FormsConstants.READONLY_REASON.OSR;
                }

                // No reason left, let's enable edit mode!
                return FormsConstants.READONLY_REASON.NONE;
            }),
        );
    }

    /**
     * Send the form to the gateway for further processing
     *
     * @param form the form to be submitted
     * @param saveMode the save mode (FINAL, INTERIM, HANDOVER)
     * @returns the observable of the form submit status
     */
    public submitMaster(form: FormState, saveMode: SUBMIT_STATUS): Observable<FormSubmitStatus> {
        return this.formsWriteService.submitMaster(form, saveMode).pipe(
            mergeMap(() => {
                return this.formsStorageService.saveFormSubmitStatus(
                    form.notification,
                    form.formId,
                    saveMode,
                    form.materialKey ?? '',
                );
            }),
        );
    }

    public submitParticipantCopies(
        form: FormState,
        participantCopyIds: ParticipantCopyId[],
    ): Observable<void | CONNECTION_ERROR_CODES> {
        return this.formsWriteService.submitParticipantCopies(form, participantCopyIds);
    }

    public refreshXML(form: FormIdentifier): Observable<unknown> {
        return this.formsWriteService.refreshXML(form);
    }

    /**
     * Delete any cached forms and the cached form list for a notification (but only if there are no more notifications
     * for that equipment on the device!)
     *
     * @param formList the form list to be cleaned up
     * @returns the observable of the form list
     */
    public cleanupFormList(formList: FormList): Observable<FormList> {
        // check if no other notifications exist for this equipment. If yes, then delete all cached forms. Otherwise just
        // delete the cached state, but keep the odata xml cache
        let deleteOdataCache = true;

        const notificationsForEquipment = this.notificationService
            .getNotificationStateByEquipment(formList.equipmentId)
            .filter(
                (notification) =>
                    !(
                        notification.updatedStatus.statusCode === NOTIFICATION_STATUS_CODE.CRM ||
                        notification.updatedStatus.statusCode === NOTIFICATION_STATUS_CODE.CNCL
                    ),
            );

        if (notificationsForEquipment.length > 1) {
            this.log.debug(
                `FormsService: cleanupFormList: There are other notifications for equipment '${formList.equipmentId}'`,
            );
            deleteOdataCache = false;
        }

        this.log.debug(
            `FormsService: cleanupFormList: Cleaning up forms for equipment ${formList.equipmentId}, ${
                deleteOdataCache ? 'not' : ''
            } keeping the odata cache`,
        );

        // forkJoin on an empty array does not trigger the pipe by calling next(), instead it ust completes and
        // the pipe is never triggered. To fix this, we just return an empty array instead.
        const formList$ =
            formList.entries.length > 0
                ? forkJoin(
                      formList.entries.map((entry) =>
                          this.formsStorageService.removeFormStateFromCache(entry.id, formList.notification).pipe(
                              mergeMap(() =>
                                  this.formsStorageService.removeFormSubmitStatusForForm(
                                      entry.id,
                                      formList.notification,
                                  ),
                              ),
                              mergeMap(() =>
                                  deleteOdataCache
                                      ? this.formsStorageService.removeFormXmlFromCache(entry.id, formList.notification)
                                      : of(undefined),
                              ),
                          ),
                      ),
                  )
                : of([]);

        // Load stored form list
        return formList$.pipe(
            mergeMap(() =>
                deleteOdataCache
                    ? this.formsStorageService.removeFormListOdataCache(
                          formList.materialId,
                          formList.equipmentId,
                          formList.notification,
                      )
                    : of(undefined),
            ),
            mergeMap(() => this.formsStorageService.removeFormSubmitStatus(formList.notification)),
            mergeMap(() => this.formsStorageService.removeEquipmentSetOdataFromCache(formList.notification)),
            map(() => ({
                ...formList,
                entries: formList.entries.map((entry) => ({
                    ...entry,
                    cached: deleteOdataCache ? false : entry.cached,
                })),
            })),
        );
    }

    /**
     * Check the submission state of forms for a particular notification and display appropriate warnings if a form was
     * submitted as handover or if no forms were submitted at all
     *
     * @param notificationId the notification identifier
     * @returns the observable of the form status warnings
     */
    public checkFormStatus(notificationId: NotificationIdentifier): Observable<FormStatusWarnings> {
        return this.formsStorageService.loadFormSubmitStatus(notificationId).pipe(
            map((formStates) => {
                // These are just the states of the individual forms, regardless of which form it actually was
                const states = Object.values(formStates);

                const warnings = new FormStatusWarnings();

                // Check if we have records of forms which might not have been submitted
                warnings.noFormSubmitted =
                    states.length > 0 &&
                    states.filter((status) =>
                        [
                            SUBMIT_STATUS.FINAL,
                            SUBMIT_STATUS.FINAL_NOT_OK,
                            SUBMIT_STATUS.INTERIM,
                            SUBMIT_STATUS.INTERIM_NOT_OK,
                            SUBMIT_STATUS.HANDOVER,
                        ].includes(status),
                    ).length === 0;

                // With the assumption that at a time user can assess & hand-over only one form
                warnings.handOverFormName =
                    Object.keys(formStates).find((key) => formStates[key] === SUBMIT_STATUS.HANDOVER) || '';

                warnings.openFormName =
                    Object.keys(formStates).find((key) => formStates[key] === SUBMIT_STATUS.EDITED) || '';

                // If no form submit states exist at all, then we did definitely no submit a form!
                warnings.noFormSubmitted = warnings.noFormSubmitted || states.length === 0;

                return warnings;
            }),
        );
    }

    /**
     * Save the current form state to storage
     *
     * @param form the form state to be saved
     * @returns the observable of the form state
     */
    public save(form: FormState): Observable<FormState> {
        return this.formsStorageService.saveFormStateToCache(form);
    }

    public saveLastOpenedForm(lastOpenedForm: LastOpenedFormState): Observable<LastOpenedFormState> {
        return this.formsStorageService.saveLastOpenedForm(lastOpenedForm);
    }

    public removeFormFromCacheCompletely(
        form: FormIdentifier,
        notification: NotificationIdentifier,
    ): Observable<FormSubmitStatus> {
        return this.formsStorageService.removeFormXmlFromCache(form, notification).pipe(
            mergeMap(() => this.formsStorageService.removeFormStateFromCache(form, notification)),
            mergeMap(() => this.formsStorageService.removeFormSubmitStatusForForm(form, notification)),
        );
    }

    // SPQ data is stored both in FormState cache and SPQstate c ache
    // This function will clean up both states from cache
    public removeSpqFormFromCache(
        spqMaterials: Array<{ form: FormIdentifier; notificationId: NotificationIdentifier; materialKey: string }>,
    ): Observable<Array<void | RemoveResult>> {
        this.log.debug(`FormsService: removeSpqFormFromCache: removing spq related data from cache`);
        return forkJoin(
            spqMaterials.map((spqMaterial) => {
                return this.servicePartQuestionnaireService
                    .removeQuestionnaireCopyDataFromCache(
                        spqMaterial.notificationId,
                        spqMaterial.form,
                        spqMaterial.materialKey,
                    )
                    .pipe(
                        mergeMap(() =>
                            this.formsStorageService.removeFormStateFromCache(
                                spqMaterial.form,
                                spqMaterial.notificationId,
                            ),
                        ),
                    );
            }),
        ).pipe(take(1));
    }

    public removeSafetyAssessmentFormFromCache(
        form: FormIdentifier,
        notificationId: NotificationIdentifier,
    ): Observable<void | RemoveResult> {
        this.log.debug(
            `FormsService: removeSafetyAssessmentFormFromCache: removing SafetyAssessmentForm related data from cache`,
        );
        return this.formsStorageService.removeSAFSubmitStatus(notificationId, form).pipe(
            mergeMap(() => this.formsStorageService.removeFormStateFromCache(form, notificationId)),
            mergeMap(() => this.formsStorageService.removeSAFXmlFromCache(form, notificationId)),
        );
    }

    public checkSafetyAssessmentFormInCache(
        form: FormIdentifier,
        notificationId: NotificationIdentifier,
    ): Observable<boolean> {
        this.log.debug(
            `FormsService: checkSafetyAssessmentFormInCache: removing SafetyAssessmentForm related data from cache`,
        );
        return this.formsStorageService.isSAFDataInCache(form, notificationId);
    }
}
